import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { LocalDateTime } from 'js-joda';
import { makeStyles } from '@material-ui/core/styles';
import DashboardDataTable from 'modules/common/dashboardDataTable.component';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

const useStyles = makeStyles((theme) => ({
  filtersContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  formControl: {
    minWidth: 200,
  },
  searchField: {
    minWidth: 250,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  noComments: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  clickableLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  commentTextCell: {
    maxWidth: 400,
    cursor: 'pointer',
  },
}));

const ExternalCommentsGrid = ({ comments, isLoading, clients }) => {
  const classes = useStyles();

  // Filter state (moved internally)
  const [filters, setFilters] = useState({
    dateRange: null,
    clientId: null,
    searchText: null,
  });

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const dt = LocalDateTime.parse(dateString);
      const month = dt.month().toString().substring(0, 3);
      const day = dt.dayOfMonth();
      const year = dt.year();
      const hour = dt.hour() % 12 || 12;
      const minute = dt.minute().toString().padStart(2, '0');
      const ampm = dt.hour() >= 12 ? 'PM' : 'AM';
      return `${month} ${day}, ${year} ${hour}:${minute} ${ampm}`;
    } catch (error) {
      return dateString;
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      dateRange: null,
      clientId: null,
      searchText: null,
    });
  };

  // Filter comments (sorting is handled by the grid)
  const filteredComments = useMemo(() => {
    let filtered = [...comments];

    // Apply search text filter
    if (filters.searchText && filters.searchText.trim()) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter((comment) =>
        comment.commentText?.toLowerCase().includes(searchLower),
      );
    }

    // TODO: Client filter disabled - API doesn't return clientId in comment response
    // Re-enable when backend adds clientId field to comment DTOs
    if (filters.clientId) {
      // Note: API doesn't return clientId, so we can't filter by it
      // This would need to be done server-side or we need clientId in response
    }

    // Apply date range filter
    if (filters.dateRange) {
      const now = new Date();
      const threshold = new Date();

      if (filters.dateRange === '7') {
        threshold.setDate(now.getDate() - 7);
      } else if (filters.dateRange === '30') {
        threshold.setDate(now.getDate() - 30);
      }

      if (filters.dateRange === '7' || filters.dateRange === '30') {
        filtered = filtered.filter((comment) => {
          const commentDate = new Date(comment.createdDate);
          return commentDate >= threshold;
        });
      }
    }

    return filtered;
  }, [comments, filters]);

  const columns = [
    {
      accessorKey: 'createdDate',
      header: 'Created Date',
      Cell: ({ cell }) => formatDateTime(cell.getValue()),
    },
    {
      accessorKey: 'authId',
      header: 'Auth ID',
      Cell: ({ row }) => (
        <a
          href={`/authorizations/${row.original.fileId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.clickableLink}
        >
          {row.original.authId || '-'}
        </a>
      ),
    },
    {
      accessorKey: 'eeName',
      header: 'EE Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'clientName',
      header: 'Client Name',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      accessorKey: 'commentText',
      header: 'Comment Text',
      enableSorting: false,
      muiTableBodyCellProps: { className: classes.commentTextCell },
      Cell: ({ cell }) => (
        <Tooltip title={cell.getValue() || ''} arrow>
          <span>{truncateText(cell.getValue(), 100)}</span>
        </Tooltip>
      ),
    },
    {
      accessorKey: 'createdByName',
      header: 'Created By',
      Cell: ({ cell }) => cell.getValue() || '-',
    },
  ];

  if (isLoading) {
    return (
      <Paper elevation={0} className={classes.tableContainer}>
        <Typography className={classes.noComments} color="textSecondary">
          Loading external comments...
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      {/* Filter UI */}
      <Box className={classes.filtersContainer}>
        {/* Date Range Filter */}
        <FormControl className={classes.formControl} size="small" disabled={isLoading}>
          <InputLabel id="comment-date-filter-label">Date Range</InputLabel>
          <Select
            labelId="comment-date-filter-label"
            id="comment-date-filter"
            value={filters.dateRange || ''}
            onChange={(e) => handleFilterChange('dateRange', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
          </Select>
        </FormControl>

        {/* Client Filter - Disabled due to missing clientId in API response */}
        <FormControl className={classes.formControl} size="small" disabled>
          <InputLabel id="comment-client-filter-label">Client</InputLabel>
          <Select
            labelId="comment-client-filter-label"
            id="comment-client-filter"
            value={filters.clientId || ''}
            onChange={(e) => handleFilterChange('clientId', e.target.value || null)}
          >
            <MenuItem value="">
              <em>All Clients (Disabled)</em>
            </MenuItem>
            {clients.map((client) => (
              <MenuItem key={client.clientId} value={client.clientId}>
                {client.clientName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Search Box */}
        <TextField
          className={classes.searchField}
          size="small"
          label="Search Comment Text"
          variant="outlined"
          value={filters.searchText || ''}
          onChange={(e) => handleFilterChange('searchText', e.target.value || null)}
          disabled={isLoading}
        />

        {/* Clear Filters Button */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearFilters}
          disabled={isLoading}
        >
          Clear Filters
        </Button>
      </Box>

      {/* Grid */}
      {comments.length === 0 ? (
        <Paper elevation={0} className={classes.tableContainer}>
          <Typography className={classes.noComments} color="textSecondary">
            No external comments found
          </Typography>
        </Paper>
      ) : filteredComments.length === 0 ? (
        <Paper elevation={0} className={classes.tableContainer}>
          <Typography className={classes.noComments} color="textSecondary">
            No comments match the current filters
          </Typography>
        </Paper>
      ) : (
        <Box className={classes.tableContainer}>
          <DashboardDataTable
            columns={columns}
            data={filteredComments}
            initialSorting={[{ id: 'createdDate', desc: true }]}
          />
        </Box>
      )}
    </>
  );
};

ExternalCommentsGrid.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.shape({
    commentId: PropTypes.number.isRequired,
    fileId: PropTypes.number.isRequired,
    authId: PropTypes.string.isRequired,
    eeName: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    commentText: PropTypes.string.isRequired,
    createdDate: PropTypes.string.isRequired,
    createdByName: PropTypes.string.isRequired,
  })).isRequired,
  isLoading: PropTypes.bool,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      clientId: PropTypes.number,
      clientName: PropTypes.string,
    }),
  ),
};

ExternalCommentsGrid.defaultProps = {
  isLoading: false,
  clients: [],
};

export default ExternalCommentsGrid;
